import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import type {
  PaymentHistoryResponse,
  AppliedFilters,
  PaymentHistoryRequest,
  TransactionDetailResponse,
  UpdatePaymentCategoryResponse,
  UpdatePaymentNoteResponse,
  AddPaymentReferenceResponse,
} from '../models';
import { TransactionApi } from '../apis';

/**
 * Transaction Service
 * Service layer that wraps the Transaction API
 *
 * Responsibilities:
 * - Business logic and data transformations
 * - Transform UI filters to API payload structure
 * - Date calculations for preset filters
 * - Abstraction layer between store and HTTP layer
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private readonly transactionApi = inject(TransactionApi);

  /**
   * Load transactions with optional filters
   * Handles transformation from UI filters to API payload
   *
   * @param page - Page number (1-indexed)
   * @param pageSize - Items per page
   * @param filters - Optional UI filters to apply
   * @returns Observable<PaymentHistoryResponse>
   */
  loadTransactions(
    page: number,
    pageSize: number,
    filters?: AppliedFilters,
  ): Observable<PaymentHistoryResponse> {
    const payload = this.buildApiPayload(page, pageSize, filters);
    return this.transactionApi.getPaymentHistory(payload);
  }

  /**
   * Transform UI filters to API payload structure
   * This is where business logic lives - not in Store or API
   *
   * @private
   */
  private buildApiPayload(
    page: number,
    pageSize: number,
    filters?: AppliedFilters,
  ): Partial<PaymentHistoryRequest> {
    const payload: Partial<PaymentHistoryRequest> = {
      page,
      limit: pageSize,
    };

    if (!filters) {
      return payload;
    }

    // Every filter the endpoint understands is nested here — sending any of them at
    // the root of the body is silently ignored and yields zero rows.
    const apiFilters: PaymentHistoryRequest['filters'] = {};

    // 1. Date filters (inside filters object)
    if (filters.dateRange) {
      this.applyDateFilters(apiFilters, filters.dateRange);
    }

    // 2. Status filter (inside filters object)
    if (filters.status?.length) {
      apiFilters.status = filters.status;
    }

    // 3. Transaction direction - Débit/Crédit
    if (filters.transactionDirection?.length) {
      apiFilters.transaction_direction = filters.transactionDirection;
    }

    // 4. Counterpart accounts - Clients (values are account *numbers*)
    if (filters.counterpartIds?.length) {
      apiFilters.counterpart_account_ids = filters.counterpartIds;
    }

    // 5. Transaction types (convert number[] to string[])
    if (filters.transactionTypes?.length) {
      apiFilters.transaction_type = filters.transactionTypes.map(String);
    }

    // 6. Categories
    if (filters.categoryIds?.length) {
      apiFilters.category_ids = filters.categoryIds;
    }

    // 7. Internal accounts
    if (filters.accountIds?.length) {
      apiFilters.account_ids = filters.accountIds;
    }

    // 8. Search term — routed into filters object by mode
    if (filters.searchTerm?.trim()) {
      const term = filters.searchTerm.trim();
      if (filters.searchMode === 'payment_id') {
        apiFilters.payment_id = term;
      } else if (filters.searchMode === 'title') {
        apiFilters.title = term;
      } else {
        // default: reference
        apiFilters.reference = [term];
      }
    }

    // Only include filters object if it has any keys
    if (Object.keys(apiFilters).length > 0) {
      payload.filters = apiFilters;
    }

    return payload;
  }

  /**
   * Apply date range filters into the filters object (preset or custom).
   * The backend reads date bounds from inside `filters`, alongside `status`.
   * Extracted to reduce cognitive complexity of buildApiPayload.
   *
   * @private
   */
  private applyDateFilters(
    apiFilters: NonNullable<PaymentHistoryRequest['filters']>,
    dateRange: NonNullable<AppliedFilters['dateRange']>,
  ): void {
    if (dateRange.preset) {
      const dates = this.calculatePresetDates(dateRange.preset);
      apiFilters.start_date = dates.startDate;
      apiFilters.end_date = dates.endDate;
      return;
    }
    if (dateRange.startDate) apiFilters.start_date = dateRange.startDate;
    if (dateRange.endDate) apiFilters.end_date = dateRange.endDate;
  }

  /**
   * Calculate date range from preset
   * Pure business logic for date calculations
   *
   * @private
   */
  private calculatePresetDates(preset: string): { startDate: string; endDate: string } {
    const today = new Date();
    const endDate = this.formatDate(today);
    let startDate: string;

    switch (preset) {
      case 'last_7_days':
      case '7_days':
        startDate = this.formatDate(this.subtractDays(today, 7));
        break;

      case 'last_30_days':
      case '30_days':
        startDate = this.formatDate(this.subtractDays(today, 30));
        break;

      case 'last_month': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          startDate: this.formatDate(lastMonth),
          endDate: this.formatDate(lastMonthEnd),
        };
      }

      case 'current_month':
      case 'this_month': {
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = this.formatDate(currentMonthStart);
        break;
      }

      default:
        // Default to last 30 days
        startDate = this.formatDate(this.subtractDays(today, 30));
    }

    return { startDate, endDate };
  }

  /**
   * Format date to ISO string (YYYY-MM-DD)
   * Uses local timezone to avoid UTC conversion bugs
   * @private
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Subtract days from a date
   * @private
   */
  private subtractDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  /**
   * Load transaction detail by payment ID
   * Delegates to API layer - no transformation needed for dynamic UI
   *
   * @param paymentId - Transaction/payment ID from route
   * @returns Observable<TransactionDetailResponse>
   */
  getTransactionDetail(paymentId: string): Observable<TransactionDetailResponse> {
    return this.transactionApi.getTransactionDetail(paymentId);
  }

  /**
   * Update the category assigned to a payment
   *
   * @param paymentId - The payment to update
   * @param categoryId - New category ID
   * @returns Observable<UpdatePaymentCategoryResponse>
   */
  updatePaymentCategory(
    paymentId: string,
    categoryId: string,
  ): Observable<UpdatePaymentCategoryResponse> {
    return this.transactionApi.updatePaymentCategory(paymentId, categoryId);
  }

  /**
   * Update the shared note of a payment
   *
   * @param paymentId - The payment to update
   * @param note - Note text to save
   * @returns Observable<UpdatePaymentNoteResponse>
   */
  updatePaymentNote(paymentId: string, note: string): Observable<UpdatePaymentNoteResponse> {
    return this.transactionApi.updatePaymentNote(paymentId, note);
  }

  /**
   * Add a reference file to a payment.
   *
   * @param paymentId - The payment to attach the reference to
   * @param refNumber - Reference label entered by the user
   * @param file - Attached file (JPEG, PNG, or PDF)
   * @returns Observable<AddPaymentReferenceResponse>
   */
  addPaymentReference(
    paymentId: string,
    refNumber: string,
    file: File,
  ): Observable<AddPaymentReferenceResponse> {
    return this.transactionApi.addPaymentReference(paymentId, refNumber, file);
  }
}
